/**
 * The GloryBoard point adjustment, as data rather than as a screen.
 *
 * Everything here is pure and React-free so the rules an administrator is held to can be tested
 * without mounting anything. The screen that uses it is `GloryBoardPointAdjustment.js`.
 */

/**
 * A fan reached from the ranked board. The row's own `_id` is the TeamReward document — the user
 * is `userId`, and sending the wrong one to the balance endpoint returns a silent zero.
 */
export const participantFromBoardRow = (row) => ({
  userId: row.userId,
  // RewardHistoryModal guards its fetch on `user?._id` and builds its URL from `user.userId`, so
  // both must be present and both must be the USER id. It also depends on `user` by identity, so
  // this object has to be built once here rather than spread into a literal at render time —
  // a fresh object each render refires that effect forever and locks the tab.
  _id: row.userId,
  name: row.name,
  username: row.username,
  avatar: row.avatar,
  rank: row.currentRank,
  band: row.band,
  fromBoard: true
});

/** A fan reached from the user search, who may have no standing on the open team at all. */
export const participantFromSearch = (user) => ({
  userId: user._id,
  _id: user._id,
  name: user.name,
  username: user.username,
  avatar: user.avatar,
  rank: null,
  band: null,
  fromBoard: false
});

/**
 * A fan with no TeamReward row and a fan whose balance is genuinely zero are indistinguishable in
 * the response — both come back as `totalPoints: 0`. Anyone reached from the board necessarily has
 * a row, so only a searched fan at zero can be said to be absent from it.
 */
export const isOnThisBoard = (participant, totalPoints) =>
  Boolean(participant?.fromBoard) || totalPoints > 0;

/**
 * The two business actions. Both reduce the balance; they differ in why the points left.
 *
 * These names appear nowhere in the backend — the reward ledger knows `redeem` and
 * `admin_adjustment`, which are categories of entry rather than administrative intent. How these
 * two are finally stored is the adjustment endpoint's decision, not this screen's.
 */
export const ACTION_TYPES = {
  REDEEM: 'ADMIN_REDEEM',
  DEDUCT: 'ADMIN_DEDUCT'
};

/**
 * Both actions the requirement defines. Redeeming subtracts points the fan has spent; deducting
 * subtracts them as a correction or penalty. Neither grants anything — the requirement is explicit
 * that there is "no prize redemption, no winner logic", only balance management.
 */
export const ALL_ACTION_OPTIONS = [
  {
    code: ACTION_TYPES.REDEEM,
    label: 'Redeem Points',
    description: 'Subtract points the participant has spent'
  },
  {
    code: ACTION_TYPES.DEDUCT,
    label: 'Deduct Points',
    description: 'Subtract points as a correction or penalty'
  }
];

/**
 * DEFERRED: Deduct Points is withheld until the penalty rules are agreed — the workstream 6 review
 * asked for it to wait until after P4P Day. Restoring it is this one line, and the form brings its
 * chooser back on its own when a second action becomes available.
 */
export const DEFERRED_ACTIONS = [ACTION_TYPES.DEDUCT];

export const ACTION_OPTIONS = ALL_ACTION_OPTIONS.filter(
  (action) => !DEFERRED_ACTIONS.includes(action.code)
);

/**
 * The reason codes the server defines and validates.
 *
 * These mirror `PointAdjustmentReason` in the backend's `constants/pointAdjustment.ts`, which is
 * now the authority: a code outside that list is refused before the handler runs. They are no
 * longer provisional, but they are also no longer this file's to change alone — adding one means
 * adding it to the backend enum in the same change.
 *
 * The chosen code reaches the audit record and stops there. It is deliberately never written onto
 * the ledger row, so the participant is never shown the category their adjustment was filed under.
 *
 * Display text lives in `value` because Dropdown renders `option.value`; `code` is the stable key.
 */
export const REASON_OPTIONS = [
  { code: 'correction', value: 'Correction' },
  { code: 'reward_redemption', value: 'Reward Redemption' },
  { code: 'duplicate_award', value: 'Duplicate Award' },
  { code: 'fraud_or_abuse', value: 'Fraud or Abuse' },
  { code: 'other', value: 'Other' }
];

/**
 * Whole points only, and strictly positive. Returns the message an administrator should read, or
 * null when the amount is usable — each case names its own problem rather than saying "invalid",
 * because "invalid" does not tell anyone which of five things to change.
 */
const pointsError = (raw) => {
  const trimmed = String(raw ?? '').trim();

  if (!trimmed) return 'Points are required';
  if (!/^-?\d*\.?\d+$/.test(trimmed)) return 'Points must be a number';

  const value = Number(trimmed);

  if (!Number.isInteger(value)) return 'Points must be a whole number';
  if (value <= 0) return 'Points must be greater than zero';

  return null;
};

/** The parsed amount, or null when it is not a usable number. */
export const parsePoints = (raw) => (pointsError(raw) ? null : Number(String(raw).trim()));

/**
 * The whole draft as derived data: what is wrong with it, what it would do, and whether it may be
 * submitted. Evaluated on every keystroke, so the Apply button is a function of the draft rather
 * than something a submit handler decides after the fact.
 */
export const deriveAdjustment = ({
  participant,
  actionType,
  points,
  reasonCode,
  totalPoints = 0
}) => {
  const pointsValue = parsePoints(points);
  const resultingBalance = pointsValue === null ? null : totalPoints - pointsValue;
  const exceedsBalance = pointsValue !== null && pointsValue > totalPoints;

  const errors = {
    // An amount above the balance is a hard stop, not a warning. The workstream 6 requirement
    // names both the rule and the words: "If deduction amount exceeds current balance -> show
    // 'Insufficient Points'". An earlier draft of this screen warned and let the administrator
    // continue, on the reasoning that a floor was the backend's policy to set. It had already
    // been set.
    points: pointsError(points) || (exceedsBalance ? 'Insufficient Points' : null),
    action: actionType ? null : 'Choose an action',
    reason: reasonCode ? null : 'A reason is required'
  };

  return {
    errors,
    pointsValue,
    resultingBalance,
    exceedsBalance,
    isComplete: Boolean(participant) && !errors.points && !errors.action && !errors.reason
  };
};

/**
 * A fresh identifier for one confirmed adjustment.
 *
 * Generated once when the administrator confirms and reused on every retry of that confirmation:
 * the server applies an identifier at most once, so a retry after an unanswered request replays
 * the original outcome instead of subtracting a second time. Generating a new one on retry would
 * defeat that entirely.
 *
 * `crypto.randomUUID` is not present in every environment this bundle runs in — notably older
 * jsdom under test — so there is a fallback rather than an optional chain that yields undefined.
 */
export const newAdjustmentId = () => {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }

  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (char) => {
    const random = (Math.random() * 16) | 0;
    const value = char === 'x' ? random : (random & 0x3) | 0x8;
    return value.toString(16);
  });
};

/**
 * The message an administrator reads for each way an adjustment can be refused.
 *
 * Keyed by the `code` the endpoint returns in its error body. Anything unrecognised is a system
 * failure, which is the one case that offers a retry — the others are decisions, not faults.
 */
export const REFUSAL_MESSAGES = {
  INSUFFICIENT_POINTS:
    'Insufficient Points. The balance moved before this was submitted — reload and try again.',
  INSUFFICIENT_PERMISSIONS: 'Insufficient Permissions. Your account cannot adjust point balances.',
  RATE_LIMITED: 'Too many adjustments in the last minute. Wait a moment and try again.',
  ADJUSTMENT_INCOMPLETE:
    "A previous attempt at this adjustment did not finish. Check the participant's history before retrying."
};

/** True when the failure is the system rather than a rule, and a retry is worth offering. */
export const isRetryable = (code) => !code || !REFUSAL_MESSAGES[code];
